import React from 'react';
import { Plus, Calendar, Edit2, Trash2 } from 'lucide-react';
import { usePreview } from '../../context/PreviewContext';
import { Button } from '../../components/ui/button';
import BloodGraphs from '../../components/BloodGraphs';

/**
 * Preview Blood Results Page
 * Read-only blood results with mock data - matches real BloodResultsPage layout
 */
const PreviewBloodResultsPage = () => {
  const { bloodResults, treatments, clinicalRanges } = usePreview();

  // Format date to match real page
  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div data-testid="blood-results-page" className="space-y-6 md:space-y-8">
      {/* Header - matching real page */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">
            My Progress
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-slate-700">See how your blood results change alongside your treatments</p>
        </div>
        <div className="flex">
          <Button disabled className="opacity-50 cursor-not-allowed bg-emerald-600">
            <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Add Blood Result
          </Button>
        </div>
      </div>

      {/* Charts Section - BloodGraphs includes SupplementTimeline internally */}
      <BloodGraphs
        results={bloodResults}
        clinicalRanges={clinicalRanges}
        supplements={treatments}
      />

      {/* Results List - matching real page layout */}
      <h2 className="text-2xl font-bold text-slate-900 mb-6">History</h2>
      <div className="space-y-4">
        {bloodResults.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border border-slate-100 shadow-sm text-center">
            <Calendar className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No results yet</h3>
            <p className="text-slate-600 mb-6">Start tracking by adding your first blood test result</p>
          </div>
        ) : (
          bloodResults.slice().reverse().map((result) => (
            <div
              key={result.id}
              className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-lg font-semibold text-slate-900">{formatDisplayDate(result.date)}</p>
                  </div>
                  {result.notes && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-blue-800 mb-1">Notes:</p>
                      <p className="text-sm text-blue-900">{result.notes}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    disabled
                    className="p-2 text-slate-300 cursor-not-allowed rounded-lg"
                    title="Edit result (Preview mode)"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    disabled
                    className="p-2 text-slate-300 cursor-not-allowed rounded-lg"
                    title="Delete result (Preview mode)"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {result.wbc !== null && result.wbc !== undefined && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">WBC</p>
                    <p className="text-lg font-semibold text-slate-900">{result.wbc.toFixed(1)}</p>
                  </div>
                )}
                {result.lymphocytes !== null && result.lymphocytes !== undefined && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Lymphocytes</p>
                    <p className="text-lg font-semibold text-slate-900">{result.lymphocytes.toFixed(1)}</p>
                  </div>
                )}
                {result.platelets !== null && result.platelets !== undefined && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Platelets</p>
                    <p className="text-lg font-semibold text-slate-900">{result.platelets}</p>
                  </div>
                )}
                {result.haemoglobin !== null && result.haemoglobin !== undefined && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Haemoglobin</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {clinicalRanges?.haemoglobin?.unit === 'g/dL' 
                        ? result.haemoglobin.toFixed(1) 
                        : Math.round(result.haemoglobin)}
                    </p>
                  </div>
                )}
                {result.neutrophils !== null && result.neutrophils !== undefined && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Neutrophils</p>
                    <p className="text-lg font-semibold text-slate-900">{result.neutrophils.toFixed(1)}</p>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PreviewBloodResultsPage;
